import axios from 'axios';
import { store } from '../store/store';
import { logout } from '../features/user/userSlice';
import { fetchAuthSession } from 'aws-amplify/auth';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const state = store.getState();
    const user = state.user?.user;

    if (user) {
      try {
        // First try to get token from AWS Amplify session (for OAuth users)
        const session = await fetchAuthSession();

        if (session.tokens && session.tokens.idToken) {
          const token = session.tokens.idToken.toString();
          config.headers.Authorization = token;
          return config;
        }
      } catch (amplifyError) {
        // If Amplify session fails, fall back to localStorage (for email/password users)
      }

      // Fallback: Try to get token from localStorage (for email/password users)
      const aud = user.aud;
      const username = user['cognito:username'];
      if (aud && username) {
        const auth_token_key = `CognitoIdentityServiceProvider.${aud}.${username}.idToken`;
        const token = localStorage.getItem(auth_token_key);
        if (token) {
          // Check token expiration before using it
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.exp && payload.exp * 1000 < Date.now()) {
              // Token expired — force refresh via Amplify
              const refreshed = await fetchAuthSession({ forceRefresh: true });
              if (refreshed.tokens?.idToken) {
                config.headers.Authorization = refreshed.tokens.idToken.toString();
                return config;
              }
            }
          } catch (e) {
            // If parsing fails, use token as-is and let backend reject if expired
          }
          config.headers.Authorization = token;
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if error.response exists before accessing its properties
    // Skip 401 redirect if we're on the login page (during login/registration flow)
    if (error?.response && error?.response?.status === 401 && !window.location.pathname.includes('/login')) {
      store.dispatch(logout()); // Clear user data from the store
      window.location.replace('/login'); // Redirect to login page
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
